import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { NotificationService } from '../services/notification.service';
import { AuthenticationService } from '../../features/auth/services/auth.service';

/**
 * HTTP Error Interceptor
 * Catches HTTP errors and shows user-friendly notifications.
 * Handles 401 errors by logging the user out.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const notificationService = inject(NotificationService);
  const authService = inject(AuthenticationService);

  // Endpoints that should handle their own errors (don't show global notification)
  const silentEndpoints = [
    'connect/token', // Login errors handled by login component
    'api/Users/forgotreqs', // Forgot password errors handled by forgot-password component
  ];

  // Endpoints that are silent only for specific HTTP methods
  const silentGetEndpoints = [
    /Claims\/\d+$/, // GET /Claims/{id} - search claim errors handled by navigation component
  ];

  const isSilentGet = req.method === 'GET' && silentGetEndpoints.some(pattern => pattern.test(req.url));

  const isSilent = silentEndpoints.some(url => req.url.includes(url));

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Handle 401 Unauthorized - session expired
      if (error.status === 401) {
        notificationService.error('Your session has expired. Please log in again.');
        authService.logout();
        return throwError(() => error);
      }

      // Show notification for non-silent endpoints
      if (!isSilent && !isSilentGet) {
        const message = getErrorMessage(error);
        notificationService.error(message);
      }

      return throwError(() => error);
    })
  );
};

/**
 * Extract user-friendly error message from HTTP error response
 * Priority order: details > detail > message > title > validation errors > status-based
 */
function getErrorMessage(error: HttpErrorResponse): string {
  // Check for custom error message from API
  if (error.error) {
    // .NET API error format
    if (typeof error.error === 'string') {
      return error.error;
    }
    // Priority 1: "details" field (custom API format)
    if (error.error.details) {
      return error.error.details;
    }
    // Priority 2: "detail" field (.NET Problem Details format)
    if (error.error.detail) {
      return error.error.detail;
    }
    // Priority 3: "message" field
    if (error.error.message) {
      return error.error.message;
    }
    // Priority 4: "title" field
    if (error.error.title) {
      return error.error.title;
    }
    // Priority 5: Validation errors
    if (error.error.errors) {
      const errors = error.error.errors;
      const firstError = Object.values(errors)[0];
      if (Array.isArray(firstError) && firstError.length > 0) {
        return firstError[0] as string;
      }
    }
  }

  // Fallback to status-based messages
  switch (error.status) {
    case 0:
      return 'Unable to connect to server. Please check your internet connection.';
    case 400:
      return 'Invalid request. Please check your input.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return 'The requested resource was not found.';
    case 409:
      return 'A conflict occurred. Please refresh and try again.';
    case 422:
      return 'The data provided is invalid.';
    case 429:
      return 'Too many requests. Please wait a moment and try again.';
    case 500:
      return 'Server error. Please try again later.';
    case 502:
    case 503:
    case 504:
      return 'Service temporarily unavailable. Please try again later.';
    default:
      return error.statusText || 'An unexpected error occurred.';
  }
}
